import { TableCell, TableRow } from '@mui/material';
import { PencilSimple, PlusCircle, Trash } from '@phosphor-icons/react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { message, Popconfirm } from 'antd';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

import useTranslation from 'customHooks/translate';
import { DynamicTable } from 'components/dynamicTable';
import { Flex } from 'components/flex';
import { PencilWrapper, TrashWrapper } from 'pages/Invoices/styled';
import {
  getMeasurementUnits,
  getSalesTypes,
  getTaxTypes,
  sendFinishEmail,
} from 'services/collections';
import {
  createCostLine,
  finishCost,
  getCostLines,
  updateCostLine,
  deleteCostLine,
} from 'services/invoices';
import { toastError } from 'services/helpers/getError';
import { queryClient } from 'config/query-client';
import { CostLineCustomError } from 'types';
import {
  COST_LINES,
  EXPENSES,
  INCOMES,
  MEASUREMENT_UNITS,
  SALES_TYPES,
  TAX_TYPES,
} from 'events/types';
import CustomDialog from 'pages/Dialogs/CustomDialog';

import '../../styles/InvoiceLine.css';
import CostLineInfo from './CostLineInfo';

export interface ItemProps {
  description: string;
  salesTypeId: number;
  price: any;
  quantity: any;
  measurementUnitId: number;
  weight: any;
  discountRate: any;
  taxTypeId: number;
  id?: number;
  onAddItem?: () => void;
}

const defaultValues: ItemProps = {
  salesTypeId: 0,
  taxTypeId: 0,
  quantity: 0,
  measurementUnitId: 0,
  description: '',
  price: 0,
  discountRate: 0,
  weight: 0,
};

const CostLine = () => {
  const navigate = useNavigate();
  const translation = useTranslation();

  const [showModal, setShowModal] = useState(false);
  const [lineToEdit, setLineToEdit] = useState<ItemProps | null>(null);
  const [partnerEmail, setPartnerEmail] = useState<string>();
  const [formData, setFormData] = useState<ItemProps>(defaultValues);
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>(
    {}
  );
  const [recipientEmail2, setRecipientEmail2] = useState<string>('');

  // Create refs for all form inputs to enable keyboard navigation
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const salesTypeIdRef = useRef<HTMLSelectElement>(null);
  const taxTypeIdRef = useRef<HTMLSelectElement>(null);
  const priceRef = useRef<HTMLInputElement>(null);
  const quantityRef = useRef<HTMLInputElement>(null);
  const measurementUnitIdRef = useRef<HTMLSelectElement>(null);
  const weightRef = useRef<HTMLInputElement>(null);
  const discountRateRef = useRef<HTMLInputElement>(null);

  // Create an array of refs for navigation
  const inputRefs = [
    descriptionRef,
    salesTypeIdRef,
    taxTypeIdRef,
    priceRef,
    quantityRef,
    measurementUnitIdRef,
    weightRef,
    discountRateRef,
  ];

  // Update the handleKeyDown function to handle textarea differently
  const handleKeyDown = (e: React.KeyboardEvent, currentIndex: number) => {
    // Special handling for textarea (description field)
    if (currentIndex === 0 && e.currentTarget.tagName === 'TEXTAREA') {
      // Only move to next field on Shift+Enter for textarea
      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % inputRefs.length;
        inputRefs[nextIndex].current?.focus();
        return;
      }
      // Allow normal Enter behavior in textarea (new line)
      if (e.key === 'Enter') {
        return; // Don't prevent default, allow new line
      }
    }

    // Standard navigation for other inputs (non-textarea)
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      e.preventDefault();
      // Move to next input
      const nextIndex = (currentIndex + 1) % inputRefs.length;
      inputRefs[nextIndex].current?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Move to previous input
      const prevIndex =
        (currentIndex - 1 + inputRefs.length) % inputRefs.length;
      inputRefs[prevIndex].current?.focus();
    } else if (
      e.key === 'ArrowRight' &&
      e.currentTarget.tagName !== 'TEXTAREA'
    ) {
      if (
        e.currentTarget instanceof HTMLInputElement &&
        e.currentTarget.selectionStart === e.currentTarget.value.length
      ) {
        e.preventDefault();
        // Move to next input when cursor is at the end
        const nextIndex = (currentIndex + 1) % inputRefs.length;
        inputRefs[nextIndex].current?.focus();
      }
    } else if (
      e.key === 'ArrowLeft' &&
      e.currentTarget.tagName !== 'TEXTAREA'
    ) {
      if (
        e.currentTarget instanceof HTMLInputElement &&
        e.currentTarget.selectionStart === 0
      ) {
        e.preventDefault();
        // Move to previous input when cursor is at the beginning
        const prevIndex =
          (currentIndex - 1 + inputRefs.length) % inputRefs.length;
        inputRefs[prevIndex].current?.focus();
      }
    }
  };

  // Number input handler that maintains focus and validates input
  const handleNumberInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof ItemProps
  ) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value) || value === '') {
      setFormData({ ...formData, [field]: value });
    }
  };

  // Handle focus to clear default 0 values
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value === '0') {
      e.target.value = '';
    }
  };

  // Handle blur to restore 0 if field is empty
  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement>,
    field: keyof ItemProps
  ) => {
    if (e.target.value === '') {
      setFormData({ ...formData, [field]: 0 });
    }
  };

  const { state } = useLocation();
  const invoiceId = state?.invoiceId || null;
  const currencyId = state?.currencyId;

  const handleSendEmail = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const finalInvoiceId = invoiceId || 60;

    if (!partnerEmail) {
      toastError(translation.validEmailsRequired);
      return;
    }

    const payload = {
      invoiceId: finalInvoiceId,
      email1: partnerEmail,
      email2: recipientEmail2,
    };

    try {
      const response = await sendFinishEmail(payload);
      if (response?.data?.data === true) {
        setShowModal(false);
        message.success(translation.messages.successSent);
        navigate('/dashboard');
      } else {
        toastError(translation.emailSendFailed);
      }
    } catch (error) {
      toastError(translation.emailSendError);
    }
  };

  const { data: salesTypes } = useQuery({
    queryKey: [SALES_TYPES],
    queryFn: getSalesTypes,
    select: (response) => response.data?.data || [],
  });

  const { data: taxTypes } = useQuery({
    queryKey: [TAX_TYPES],
    queryFn: getTaxTypes,
    select: (response) => response.data?.data || [],
  });

  const { data: measurmentUnits } = useQuery({
    queryKey: [MEASUREMENT_UNITS],
    queryFn: getMeasurementUnits,
    select: (response) => response.data?.data || [],
  });

  const { data: costLines } = useQuery({
    queryKey: [COST_LINES, invoiceId],
    queryFn: () => getCostLines(invoiceId),
    select: (response) => response.data?.data || [],
  });

  useEffect(() => {
    handleReturnCreatePage();
  }, [costLines?.invoiceLines?.length]);

  const handleReturnCreatePage = () => {
    if (costLines?.invoiceLines?.length === 0) {
      setShowForm(true);
    }
  };

  const handleDeleteLine = (costLineId: number, description: string) => {
    deleteCostLine(costLineId, description)
      .then(() => {
        setFormData(defaultValues);
        setLineToEdit(null);
        setErrors({});

        queryClient.invalidateQueries({
          queryKey: [COST_LINES, invoiceId],
        });
      })
      .catch((error) => toastError(error));
  };
  const [showForm, setShowForm] = useState(
    !(costLines?.invoiceLines && costLines.invoiceLines.length > 0)
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const method = lineToEdit ? updateCostLine : createCostLine;
    method({
      ...formData,
      salesTypeId: +formData.salesTypeId,
      taxTypeId: +formData.taxTypeId,
      measurementUnitId: +formData.measurementUnitId,
      invoiceLineId: lineToEdit ? lineToEdit.id : undefined,
      invoiceId: invoiceId.data || invoiceId,
    })
      .then(() => {
        setFormData(defaultValues);
        message.success(`${translation.messages.successAdded}`);
        setShowForm(!showForm);
        setLineToEdit(null);
        queryClient.invalidateQueries({
          queryKey: [COST_LINES, invoiceId],
        });
      })
      .catch((error: CostLineCustomError) => {
        const extractedErrors = error.response?.data?.errors || {};
        error.response?.data?.Message &&
          message.error(error.response?.data?.Message);
        setErrors({
          Description: extractedErrors.Description?.[0],
          MeasurementUnitId: extractedErrors.MeasurementUnitId?.[0],
          Price: extractedErrors.Price?.[0],
          Quantity: extractedErrors.Quantity?.[0],
          SalesTypeId: extractedErrors.SalesTypeId?.[0],
          TaxTypeId: extractedErrors.TaxTypeId?.[0],
          Weight: extractedErrors.Weight?.[0],
        });
      });
  };

  useEffect(() => {
    if (lineToEdit) {
      setFormData(lineToEdit);
      setShowForm(true);
    }
  }, [lineToEdit]);

  const sendCostLine = () => {
    finishCost(invoiceId)
      .then((response) => {
        setPartnerEmail(response.data.data);
        setShowModal(true);
        [INCOMES, EXPENSES].forEach((query) => {
          queryClient.invalidateQueries({
            queryKey: [query],
            refetchType: 'all',
          });
        });
      })
      .catch((error) => {
        toastError(error);
      });
  };

  return (
    <div className="grid h-full grid-cols-1 lg:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
      <div className="col-span-2 p-[16px]">
        <div className="flex flex-wrap items-center justify-between gap-[10px]">
          <Link
            to={`/cost/new/${invoiceId}`}
            state={{ record: { invoiceId }, from: 'debitor', base: 'costLine' }}
          >
            <button className="flex items-center gap-[5px] rounded-[8px] bg-[#F6F6F6] px-[12px] py-[8px] text-[14px] font-medium text-black transition-all duration-300 ease-in-out hover:bg-gray-100 md:px-[20px]">
              <ArrowBackIosNewIcon style={{ width: '14px', height: '14px' }} />
              {translation.InvoiceDetails.Back}
            </button>
          </Link>

          {costLines?.invoiceLines?.length > 0 && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="flex items-center justify-center gap-[6px] rounded-[6px] rounded-[6px] border-2 border-[#CECECE] px-[12px] py-[8px] text-[14px] font-normal leading-[16px] text-black transition-all duration-300 ease-in-out hover:bg-gray-100 md:w-max md:px-[24px]"
            >
              <PlusCircle />
              {translation.invoicesAdd.AddLineButton}
            </button>
          )}
        </div>

        <div>
          <DynamicTable
            hasData={costLines?.invoiceLines?.length > 0}
            cells={[
              `${translation.InvoiceLine.ItemDesc}`,
              `${translation.InvoiceLine.CostType}`,
              `${translation.InvoiceLine.ItemPrice}`,
              `${translation.InvoiceLine.Quantity}`,
              `${translation.InvoiceLine.Unit}`,
              `${translation.InvoiceLine.Weight}`,
              `${translation.InvoiceLine.Discount}`,
              `${translation.InvoiceLine.TaxType}`,
            ]}
          >
            {costLines?.invoiceLines?.map((item, i) => (
              <TableRow
                key={i}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },

                  '&:nth-of-type(odd)': {
                    backgroundColor: ' rgba(70, 106, 251, 0.03);',
                  },
                }}
              >
                <TableCell width={500} align="center">
                  {item.description}
                </TableCell>
                <TableCell align="center">
                  {salesTypes?.find((s) => s.id === item.salesTypeId)?.name}
                </TableCell>
                <TableCell align="center">
                  {item.price}
                  {currencyId}
                </TableCell>
                <TableCell align="center">{item.quantity}</TableCell>
                <TableCell align="center">
                  {
                    measurmentUnits?.find(
                      (u) => u.id === item.measurementUnitId
                    )?.name
                  }
                </TableCell>
                <TableCell align="center">{item.weight}</TableCell>
                <TableCell align="center">{item.discountRate} %</TableCell>
                <TableCell align="center">
                  {taxTypes?.find((u) => u.id === item.taxTypeId)?.name}
                </TableCell>
                <TableCell align="center">
                  <Flex allItems="center" gap={10}>
                    <PencilWrapper>
                      <PencilSimple
                        size={15}
                        color="#292D32"
                        onClick={() => setLineToEdit(item)}
                      />
                    </PencilWrapper>
                    <Popconfirm
                      title={translation.DeleteItem}
                      onConfirm={() =>
                        handleDeleteLine(item.id, item.description)
                      }
                      okText="Yes"
                      cancelText="No"
                    >
                      <TrashWrapper>
                        <Trash size={15} color="#FF0000" />
                      </TrashWrapper>
                    </Popconfirm>
                  </Flex>
                </TableCell>
              </TableRow>
            ))}
          </DynamicTable>

          {showForm && (
            <form
              onSubmit={handleSubmit}
              className="mt-[20px] flex w-full flex-col gap-[12px] md:w-[80%] xl:w-[50%]"
            >
              <div className="flex flex-col gap-[12px]">
                <label className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                  {translation.InvoiceLine.ItemDesc} *
                </label>
                <textarea
                  ref={descriptionRef}
                  name="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      description: e.target.value,
                    })
                  }
                  onKeyDown={(e) => handleKeyDown(e, 0)}
                  style={{
                    width: '100%',
                    height: '150px',
                    border: '1px solid rgba(41, 45, 50, 0.25)',
                    padding: '10px',
                    borderRadius: '6px',
                    borderColor:
                      errors.Description && !formData.salesTypeId
                        ? 'red'
                        : '#ccc',
                  }}
                />
                {errors.Description && !formData.description && (
                  <span className="text-[12px] text-red-600">
                    {errors.Description}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 items-start gap-[12px] md:grid-cols-2">
                <div className="flex flex-col gap-[12px]">
                  <label className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                    {translation.InvoiceLine.CostType} *
                  </label>
                  <Select
                    ref={salesTypeIdRef}
                    name="salesTypeId"
                    value={formData.salesTypeId}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        salesTypeId: +e.target.value,
                      })
                    }
                    onKeyDown={(e) => handleKeyDown(e, 1)}
                    style={{
                      borderColor:
                        errors.SalesTypeId && !formData.salesTypeId
                          ? 'red'
                          : '',
                    }}
                  >
                    <option value="" style={{ color: 'red' }}></option>
                    {salesTypes?.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </Select>
                  {errors.SalesTypeId && !formData.salesTypeId && (
                    <span className="text-[12px] text-red-600">
                      {errors.SalesTypeId}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-[12px]">
                  <label className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                    {translation.InvoiceLine.TaxType} *
                  </label>
                  <Select
                    ref={taxTypeIdRef}
                    name="taxTypeId"
                    value={formData.taxTypeId}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        taxTypeId: +e.target.value,
                      })
                    }
                    onKeyDown={(e) => handleKeyDown(e, 2)}
                    style={{
                      borderColor:
                        errors.TaxTypeId && !formData.taxTypeId ? 'red' : '',
                    }}
                  >
                    <option value=""></option>
                    {taxTypes?.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </Select>
                  {errors.TaxTypeId && !formData.taxTypeId && (
                    <span className="text-[12px] text-red-600">
                      {errors.TaxTypeId}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-[12px]">
                  <label className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                    {translation.InvoiceLine.ItemPrice} *
                  </label>
                  <Input
                    ref={priceRef}
                    type="text"
                    name="price"
                    value={formData.price}
                    onChange={(e) => handleNumberInputChange(e, 'price')}
                    onFocus={handleFocus}
                    onBlur={(e) => handleBlur(e, 'price')}
                    onKeyDown={(e) => handleKeyDown(e, 3)}
                    style={{
                      borderColor: errors.Price && !formData.price ? 'red' : '',
                    }}
                  />
                  {errors.Price && !formData.price && (
                    <span className="text-[12px] text-red-600">
                      {errors.Price}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-[12px]">
                  <label className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                    {translation.InvoiceLine.Quantity} *
                  </label>
                  <Input
                    ref={quantityRef}
                    type="text"
                    name="quantity"
                    value={formData.quantity}
                    onChange={(e) => handleNumberInputChange(e, 'quantity')}
                    onFocus={handleFocus}
                    onBlur={(e) => handleBlur(e, 'quantity')}
                    onKeyDown={(e) => handleKeyDown(e, 4)}
                    style={{
                      borderColor:
                        errors.Quantity && !formData.quantity ? 'red' : '',
                    }}
                  />
                  {errors.Quantity && !formData.quantity && (
                    <span className="text-[12px] text-red-600">
                      {errors.Quantity}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-[12px]">
                  <label className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                    {translation.InvoiceLine.Unit} *
                  </label>
                  <Select
                    ref={measurementUnitIdRef}
                    name="measurementUnitId"
                    value={formData.measurementUnitId}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        measurementUnitId: +e.target.value,
                      })
                    }
                    onKeyDown={(e) => handleKeyDown(e, 5)}
                    style={{
                      borderColor:
                        errors.MeasurementUnitId && !formData.measurementUnitId
                          ? 'red'
                          : '',
                    }}
                  >
                    <option value=""></option>
                    {measurmentUnits?.map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.name}
                      </option>
                    ))}
                  </Select>
                  {errors.MeasurementUnitId && !formData.measurementUnitId && (
                    <span className="text-[12px] text-red-600">
                      {errors.MeasurementUnitId}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-[12px]">
                  <label className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                    {translation.InvoiceLine.Weight}
                  </label>
                  <Input
                    ref={weightRef}
                    type="text"
                    name="weight"
                    value={formData.weight}
                    onChange={(e) => handleNumberInputChange(e, 'weight')}
                    onFocus={handleFocus}
                    onBlur={(e) => handleBlur(e, 'weight')}
                    onKeyDown={(e) => handleKeyDown(e, 6)}
                    style={{
                      borderColor:
                        errors.Weight && !formData.weight ? 'red' : '',
                    }}
                  />
                  {errors.Weight && !formData.weight && (
                    <span className="text-[12px] text-red-600">
                      {errors.Weight}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-[12px]">
                  <label className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                    {translation.InvoiceLine.Discount}
                  </label>
                  <Input
                    ref={discountRateRef}
                    type="text"
                    name="discountRate"
                    value={formData.discountRate}
                    onChange={(e) => handleNumberInputChange(e, 'discountRate')}
                    onFocus={handleFocus}
                    onBlur={(e) => handleBlur(e, 'discountRate')}
                    onKeyDown={(e) => handleKeyDown(e, 7)}
                  />
                </div>
                <button
                  onClick={() => setShowForm(!showForm)}
                  className="h-max w-full rounded-[6px] bg-red-600 px-[24px] py-[12px] text-[14px] font-normal leading-[16px] text-white md:text-[16px]"
                >
                  {translation.InvoiceLine.Clear}
                </button>

                <button
                  type="submit"
                  onClick={() => {}}
                  className="h-max w-full rounded-[6px] bg-[#466afb] px-[24px] py-[12px] text-[14px] font-normal leading-[16px] text-white md:text-[16px]"
                >
                  {lineToEdit
                    ? translation.invoicesAdd.UpdateButton
                    : translation.invoicesAdd.AddButton}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {costLines?.invoiceLines.length > 0 && (
        <CostLineInfo
          currencyId={currencyId}
          costLines={costLines}
          sendCostLine={sendCostLine}
        />
      )}

      <CustomDialog
        open={showModal}
        title={translation.InvoiceLine.SuccessMessage}
        buttonTitle={translation.InvoiceLine.Send}
        onSubmit={handleSendEmail}
      >
        <NavLink to="/dashboard">
          <button className="flex items-center gap-[5px] rounded-[8px] bg-[#F6F6F6] px-[12px] py-[8px] text-[14px] font-medium text-black transition-all duration-300 ease-in-out hover:bg-gray-100 md:px-[20px]">
            <ArrowBackIosNewIcon style={{ width: '14px', height: '14px' }} />
            {translation.InvoiceLine.BackHome}
          </button>
        </NavLink>
        <form className="flex flex-col gap-[12px]">
          <div className="flex flex-col gap-[12px]">
            <label className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
              {translation.InvoiceLine.Recipient}
            </label>
            <Input
              className="d-block"
              type="email"
              maxLength={29}
              placeholder="Enter recipient email"
              value={partnerEmail}
              onChange={(e) => setPartnerEmail(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-[12px]">
            <label className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
              {translation.InvoiceLine.Recipient} 2
            </label>
            <Input
              className="d-block"
              type="email"
              id="recipientEmail2"
              placeholder="Enter recipient email"
              value={recipientEmail2}
              onChange={(e) => setRecipientEmail2(e.target.value)}
            />
          </div>
        </form>
      </CustomDialog>
    </div>
  );
};

const Input = styled.input`
  width: 100%;
  border-radius: 6px;
  padding: 12.7px 15px;
  border: 1px solid rgba(41, 45, 50, 0.25);
  :focus {
    outline: none !important;
    border: 1px solid #466afb;
    padding: 11.9px 14px;
  }
`;

const Select = styled.select`
  width: 100%;
  border-radius: 6px;
  padding: 12.7px 15px;
  border: 1px solid rgba(41, 45, 50, 0.25);
  :focus {
    outline: none !important;
    border: 1px solid #466afb;
    padding: 11.9px 14px;
  }
`;

export default CostLine;
